import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import SearchableSelect from "../../components/SearchableSelect/SearchableSelect";
import "./ReceiptSubmissionPage.css";

const API = (process.env.REACT_APP_API_URL || "") + "/api/receipts";

const emptyLineItem = () => ({ category: "", description: "", amount: "", expense_class: "" });
const displayName = c => {
  const leaf = c.name.includes(':') ? c.name.split(':').pop().trim() : c.name;
  return c.acct_num ? `${c.acct_num} ${leaf}` : leaf;
};
const displayAccountName = name => {
  if (!name) return name;
  const idx = name.lastIndexOf(':');
  return idx >= 0 ? name.slice(idx + 1).trim() : name;
};

const STEPS = ['upload','vendor','date','card','total','county','tax','items','memo','review'];
const STEP_LABELS = {
  upload:'Receipt', vendor:'Vendor', date:'Date', card:'Card',
  total:'Total', county:'Tax', tax:'Tax', items:'Items', memo:'Notes', review:'Review',
};
// 'county' collapses into 'tax' in the progress bar
const VISIBLE_STEPS = ['upload','vendor','date','card','total','tax','items','memo','review'];

export default function ReceiptSubmissionPage() {
  const [user, token, updatePreference] = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const navigate = useNavigate();
  const location = useLocation();
  const justSubmitted = location.state?.submitted;
  // Arrived from "My Missing Receipts" — the QBO expense is already known.
  const prelink = location.state?.prelink || null;

  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [cards, setCards] = useState([]);
  const [taxRef, setTaxRef] = useState({ sales_cat_id: null, produce_cat_id: null, financials_class_id: null });

  const [form, setForm] = useState({
    vendor: "",
    account: "",   // used by admin
    card_id: "",   // used by non-admin
    date: new Date().toISOString().split("T")[0],
    payment_type: "Debit",
    reference_number: "For Review",
    memo: "",
  });
  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [receiptFiles, setReceiptFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Submission prompt modal — skip it when arriving pre-linked, the context is already known
  const [showPrompt, setShowPrompt] = useState(() => !prelink && (user?.show_submission_prompt ?? true));
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const dismissPrompt = (startGuide) => {
    if (dontShowAgain) updatePreference('show_submission_prompt', false);
    setShowPrompt(false);
    if (startGuide) startWizard();
  };

  // Wizard state
  const [wizardMode, setWizardMode] = useState(false);
  const [wizardStep, setWizardStep] = useState('upload');
  const [wizardTotal, setWizardTotal] = useState("");
  const [wizardSplit, setWizardSplit] = useState(false);
  const [wizardReviewItems, setWizardReviewItems] = useState(null);
  const [taxCounty, setTaxCounty] = useState(null);   // 'bc' | 'nh' | 'other'
  const [taxCountyOther, setTaxCountyOther] = useState("");
  const [taxManual1, setTaxManual1] = useState("");
  const [taxManual2, setTaxManual2] = useState("");
  const [taxHasProduce, setTaxHasProduce] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reference/`, authHeader)
      .then(res => {
        setVendors(res.data.vendors);
        setAccounts(res.data.accounts);
        setCategories(res.data.categories);
        setClasses(res.data.classes);
        setCards(res.data.cards || []);
        setTaxRef(res.data.tax_ref || {});

        if (prelink) {
          const matchedVendor = (res.data.vendors || []).find(v => String(v.qbo_id) === String(prelink.vendor_qbo_id));
          const matchedCard = (res.data.cards || []).find(c => c.last_four === prelink.card_last_four);
          const matchedAccount = (res.data.accounts || []).find(a => String(a.qbo_id) === String(prelink.payment_account_qbo_id));
          setForm(f => ({
            ...f,
            vendor: matchedVendor ? String(matchedVendor.id) : f.vendor,
            card_id: matchedCard ? String(matchedCard.id) : f.card_id,
            account: (user?.role === 'admin' && matchedAccount) ? String(matchedAccount.id) : f.account,
            date: prelink.txn_date || f.date,
          }));
          if (prelink.total_amount != null) {
            setLineItems([{ ...emptyLineItem(), amount: String(prelink.total_amount) }]);
          }
          return;
        }

        // Auto-select if user has only one card
        if ((res.data.cards || []).length === 1) {
          setForm(f => ({ ...f, card_id: String(res.data.cards[0].id) }));
        }
        // Auto-select if admin has only one account
        if (res.data.accounts.length === 1 && user?.role === 'admin') {
          setForm(f => ({ ...f, account: String(res.data.accounts[0].id) }));
        }
      })
      .catch(err => console.error("Failed to load reference data:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed review items when entering the review step
  useEffect(() => {
    if (wizardStep === 'review') {
      setWizardReviewItems(getWizardLineItems());
    }
  }, [wizardStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-calculate 50/50 split when tax amounts change while split is active
  useEffect(() => {
    if (!wizardSplit) return;
    const total = parseFloat(wizardTotal) || 0;
    const tax1 = parseFloat(taxManual1) || 0;
    const tax2 = taxHasProduce ? (parseFloat(taxManual2) || 0) : 0;
    const subtotal = parseFloat((total - tax1 - tax2).toFixed(2));
    const effectiveSubtotal = subtotal > 0 ? subtotal : total;
    const half = parseFloat((effectiveSubtotal / 2).toFixed(2));
    setLineItems(prev => prev.map((item, i) => ({ ...item, amount: String(half) })));
  }, [taxManual1, taxManual2, taxHasProduce]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Line item helpers ---
  const updateLineItem = (index, field, value) => {
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  const TAX_CAT_DEFAULTS = { '8620': 'BC Tax 1', '8621': 'BC Tax 2' };
  const handleCategoryChange = (index, catId) => {
    const updates = { category: catId };
    const cat = categories.find(c => String(c.id) === String(catId));
    if (cat && TAX_CAT_DEFAULTS[cat.acct_num]) updates.description = TAX_CAT_DEFAULTS[cat.acct_num];
    setLineItems(prev => prev.map((item, i) => i === index ? { ...item, ...updates } : item));
  };
  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem()]);
  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== index));
  };
  const lineItemTotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  // --- File handling ---
  const addFiles = (incoming) => {
    const arr = Array.from(incoming);
    if (!arr.length) return;
    setReceiptFiles(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...arr.filter(f => !names.has(f.name))];
    });
  };
  const handleFileDrop = (e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); };
  const handleFileChange = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const removeFile = (index) => setReceiptFiles(prev => prev.filter((_, i) => i !== index));
  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(it => it.kind === 'file' && it.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    const files = imageItems.map((it, idx) => {
      const blob = it.getAsFile();
      return new File([blob], `screenshot-${Date.now()}-${idx}.png`, { type: blob.type });
    });
    addFiles(files);
  };

  // --- Wizard navigation --- (linear)
  const wizardNext = (skipTo) => {
    if (skipTo) { setWizardStep(skipTo); return; }
    const idx = STEPS.indexOf(wizardStep);
    if (idx < STEPS.length - 1) setWizardStep(STEPS[idx + 1]);
  };

  const wizardBack = () => {
    const idx = STEPS.indexOf(wizardStep);
    if (idx > 0) setWizardStep(STEPS[idx - 1]);
  };

  const startWizard = () => {
    setWizardStep('upload');
    setWizardTotal("");
    setWizardSplit(false);
    setWizardReviewItems(null);
    setTaxCounty(null);
    setTaxCountyOther("");
    setTaxManual1("");
    setTaxManual2("");
    setTaxHasProduce(false);
    setLineItems([emptyLineItem()]);
    setWizardMode(true);
  };
  const exitWizard = () => setWizardMode(false);

  // Build line items from wizard state at submit time
  const getWizardLineItems = () => {
    const total = parseFloat(wizardTotal) || 0;
    const tax1 = parseFloat(taxManual1) || 0;
    const tax2 = taxHasProduce ? (parseFloat(taxManual2) || 0) : 0;
    const subtotal = parseFloat((total - tax1 - tax2).toFixed(2));

    const prefixMap = { bc: 'BC', nh: 'NH' };
    const prefix = prefixMap[taxCounty] || taxCountyOther.trim();

    let items;
    if (wizardSplit) {
      items = lineItems
        .map(li => ({
          description: li.description || '',
          category: li.category || null,
          expense_class: li.expense_class || null,
          amount: parseFloat(li.amount) || 0,
        }))
        .filter(li => li.amount > 0);
    } else {
      items = [{
        description: lineItems[0]?.description || '',
        category: lineItems[0]?.category || null,
        expense_class: lineItems[0]?.expense_class || null,
        amount: subtotal > 0 ? subtotal : total,
      }];
    }

    if (tax1 > 0) items.push({
      description: prefix ? `${prefix} Tax 1` : 'Tax 1',
      category: taxRef?.sales_cat_id || null,
      expense_class: taxRef?.financials_class_id || null,
      amount: tax1,
    });
    if (tax2 > 0) items.push({
      description: prefix ? `${prefix} Tax 2` : 'Tax 2',
      category: taxRef?.produce_cat_id || null,
      expense_class: taxRef?.financials_class_id || null,
      amount: tax2,
    });
    return items;
  };

  const wizardProgressPct = () => {
    const activeVisible = wizardStep === 'county' ? 'tax' : wizardStep;
    const idx = VISIBLE_STEPS.indexOf(activeVisible);
    return Math.round(((idx + 1) / VISIBLE_STEPS.length) * 100);
  };

  // --- Submit ---
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");

    const isAdmin = user?.role === 'admin';

    if (!isAdmin && !form.card_id) {
      setErrorMsg("Please select a card.");
      return;
    }
    if (isAdmin && !form.account) {
      setErrorMsg("Please select an Account.");
      return;
    }
    if (!form.date) {
      setErrorMsg("Please enter a date.");
      return;
    }

    const submitLineItems = wizardMode
      ? (wizardReviewItems || getWizardLineItems()).map(li => ({ ...li, amount: parseFloat(li.amount) || 0 }))
      : lineItems.map(item => ({
      category: item.category || null,
      description: item.description,
      amount: parseFloat(item.amount) || 0,
      expense_class: item.expense_class || null,
    }));

    const hasAmount = submitLineItems.some(li => parseFloat(li.amount) > 0);
    if (!hasAmount) {
      setErrorMsg("Please enter a total purchase amount.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      if (form.vendor) fd.append('vendor', form.vendor);
      if (form.card_id) fd.append('card_id', form.card_id);
      if (isAdmin && form.account) fd.append('account', form.account);
      fd.append('date', form.date);
      fd.append('payment_type', form.payment_type);
      fd.append('reference_number', form.reference_number);
      if (form.memo) fd.append('memo', form.memo);
      fd.append('line_items', JSON.stringify(submitLineItems));
      if (wizardMode && wizardTotal) fd.append('receipt_total', wizardTotal);
      if (prelink?.purchase_id) {
        fd.append('prelinked_purchase_id', prelink.purchase_id);
        fd.append('prelinked_snapshot', JSON.stringify(prelink));
      }
      receiptFiles.forEach(f => fd.append('receipt_file', f));

      await axios.post(`${API}/submit/`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      if (user?.role === 'admin') {
        navigate("/admin", { state: { submitted: true } });
      } else {
        navigate("/dashboard", { state: { submitted: true, tab: "history" } });
      }
    } catch (err) {
      const detail = err.response?.data;
      const msg = typeof detail === "object" && detail !== null
        ? Object.values(detail).flat().join(" ")
        : (typeof detail === "string" ? detail : "Submission failed. Please try again.");
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Wizard UI ──────────────────────────────────────────────────────────────
  const renderWizard = () => {
    const pct = wizardProgressPct();
    const selectedCard = cards.find(c => String(c.id) === String(form.card_id));
    const total = parseFloat(wizardTotal) || 0;

    // Tax preview values
    const taxRate = taxCounty === 'bc' ? 0.0675 : 0.07;
    const autoTax1 = taxCounty ? parseFloat((total * taxRate).toFixed(2)) : 0;
    const autoSubtotal = total - autoTax1;
    const prefix = taxCounty === 'bc' ? 'BC' : 'NH';

    return (
      <div className="wizard-container">
        <button type="button" className="wizard-close" onClick={exitWizard} title="Cancel submission">✕</button>
        {/* Progress bar */}
        <div className="wizard-progress">
          <div className="wizard-progress-bar" style={{ width: `${pct}%` }} />
        </div>
        <div className="wizard-progress-labels">
          {VISIBLE_STEPS.map((s, i) => {
            // 'county' is a sub-step of 'tax' in the visible bar
            const activeVisible = wizardStep === 'county' ? 'tax' : wizardStep;
            const isDone = VISIBLE_STEPS.indexOf(activeVisible) > i;
            const isActive = s === activeVisible;
            return (
              <span key={s} className={`wizard-step-dot ${isActive ? 'active' : isDone ? 'done' : ''}`}>
                {STEP_LABELS[s]}
              </span>
            );
          })}
        </div>

        <div className="wizard-card">
          {/* UPLOAD */}
          {wizardStep === 'upload' && (
            <div className="wizard-step">
              <div className="wizard-question">First, attach your receipt</div>
              <div className="wizard-hint">Photo, screenshot, or PDF — drag it in, paste it, or tap to browse</div>
              <div
                className={`drop-zone ${dragOver ? "drag-over" : ""} ${receiptFiles.length ? "has-file" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
                onPaste={handlePaste}
                onClick={() => fileInputRef.current?.click()}
                tabIndex={0}
              >
                <span>Drag &amp; drop, <u>paste</u> (Ctrl+V), or <u>tap to browse</u><br />
                  <small>JPG, PNG, or PDF · Multiple files OK</small>
                </span>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple style={{ display: "none" }} onChange={handleFileChange} />
              {receiptFiles.length > 0 && (
                <ul className="wizard-file-list">
                  {receiptFiles.map((f, i) => (
                    <li key={i}>📎 {f.name} <button type="button" className="btn-link" style={{ color: "#dc3545" }} onClick={() => removeFile(i)}>Remove</button></li>
                  ))}
                </ul>
              )}
              <div className="wizard-nav">
                <button type="button" className="btn-link wizard-skip" onClick={() => wizardNext()}>Skip — attach later</button>
                <button type="button" className="btn-primary" onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* VENDOR */}
          {wizardStep === 'vendor' && (
            <div className="wizard-step">
              <div className="wizard-question">Where was this purchase made?</div>
              <div className="wizard-hint">Select the store or vendor from the list</div>
              <SearchableSelect
                options={vendors}
                value={form.vendor}
                onChange={id => setForm(f => ({ ...f, vendor: id }))}
                placeholder="-- Search vendors --"
              />
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-primary" onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* DATE */}
          {wizardStep === 'date' && (
            <div className="wizard-step">
              <div className="wizard-question">When was the purchase made?</div>
              <div className="wizard-hint">Check the date on your receipt</div>
              <input
                type="date"
                className="wizard-date-input"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              />
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-primary" disabled={!form.date} onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* CARD */}
          {wizardStep === 'card' && (
            <div className="wizard-step">
              <div className="wizard-question">Which card did you use?</div>
              <div className="wizard-hint">Select the card you paid with</div>
              {user?.role === 'admin' ? (
                <div className="wizard-card-list">
                  {accounts.map(a => (
                    <button key={a.id} type="button"
                      className={`wizard-card-option ${form.account === String(a.id) ? 'selected' : ''}`}
                      onClick={() => setForm(f => ({ ...f, account: String(a.id) }))}>
                      {displayAccountName(a.name)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="wizard-card-list">
                  {cards.map(c => (
                    <button key={c.id} type="button"
                      className={`wizard-card-option card-number ${form.card_id === String(c.id) ? 'selected' : ''}`}
                      onClick={() => setForm(f => ({ ...f, card_id: String(c.id) }))}>
                      •••• {c.last_four}
                    </button>
                  ))}
                </div>
              )}
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-primary"
                  disabled={user?.role === 'admin' ? !form.account : !form.card_id}
                  onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* TOTAL */}
          {wizardStep === 'total' && (
            <div className="wizard-step">
              <div className="wizard-question">What's the total on your receipt?</div>
              <div className="wizard-hint">Enter the full amount at the bottom of the receipt, including tax</div>
              <div className="wizard-total-block">
                <label className="wizard-total-label">Receipt Total *</label>
                <div className="wizard-total-input-wrap">
                  <span className="wizard-dollar">$</span>
                  <input type="number" step="0.01" min="0" placeholder="0.00"
                    value={wizardTotal} onChange={e => setWizardTotal(e.target.value)}
                    className="wizard-total-input" inputMode="decimal" />
                </div>
              </div>
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-primary"
                  disabled={!wizardTotal || parseFloat(wizardTotal) <= 0}
                  onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* COUNTY */}
          {wizardStep === 'county' && (
            <div className="wizard-step">
              <div className="wizard-question">Where was the purchase made?</div>
              <div className="wizard-hint">Used to label the tax lines — no math involved</div>
              <div className="wizard-card-list">
                <button type="button"
                  className={`wizard-card-option ${taxCounty === 'bc' ? 'selected' : ''}`}
                  onClick={() => { setTaxCounty('bc'); setTaxCountyOther(''); }}>
                  Brunswick County
                  <span className="wizard-card-sub">Southport area</span>
                </button>
                <button type="button"
                  className={`wizard-card-option ${taxCounty === 'nh' ? 'selected' : ''}`}
                  onClick={() => { setTaxCounty('nh'); setTaxCountyOther(''); }}>
                  New Hanover County
                  <span className="wizard-card-sub">Wilmington area</span>
                </button>
                <button type="button"
                  className={`wizard-card-option ${taxCounty === 'other' ? 'selected' : ''}`}
                  onClick={() => setTaxCounty('other')}>
                  Other
                </button>
              </div>
              {taxCounty === 'other' && (
                <input type="text" placeholder="County or location name (optional)"
                  value={taxCountyOther} onChange={e => setTaxCountyOther(e.target.value)}
                  style={{ marginTop: '0.75rem', width: '100%' }} />
              )}
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-link wizard-skip"
                  onClick={() => { setTaxCounty(null); setTaxCountyOther(''); setTaxManual1(''); setTaxManual2(''); setTaxHasProduce(false); wizardNext('items'); }}>
                  Skip taxes
                </button>
                <button type="button" className="btn-primary"
                  disabled={!taxCounty}
                  onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* TAX */}
          {wizardStep === 'tax' && (() => {
            const prefixMap = { bc: 'BC', nh: 'NH' };
            const prefix = prefixMap[taxCounty] || taxCountyOther.trim();
            const tax1Label = prefix ? `${prefix} Tax 1 — Sales Tax` : 'Sales Tax';
            const tax2Label = prefix ? `${prefix} Tax 2 — Local Food Tax` : 'Local Food Tax';
            return (
              <div className="wizard-step">
                <div className="wizard-question">Any taxes listed on your receipt?</div>
                <div className="wizard-hint">Enter the exact amounts from the receipt. Leave blank if none.</div>

                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>{tax1Label}</label>
                  <div className="wizard-total-input-wrap">
                    <span className="wizard-dollar">$</span>
                    <input type="number" step="0.01" min="0" placeholder="0.00"
                      value={taxManual1} onChange={e => setTaxManual1(e.target.value)}
                      className="wizard-total-input" inputMode="decimal" />
                  </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  <input type="checkbox" checked={taxHasProduce}
                    onChange={e => { setTaxHasProduce(e.target.checked); if (!e.target.checked) setTaxManual2(''); }} />
                  There's also a Local Food Tax line on the receipt
                </label>

                {taxHasProduce && (
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>{tax2Label}</label>
                    <div className="wizard-total-input-wrap">
                      <span className="wizard-dollar">$</span>
                      <input type="number" step="0.01" min="0" placeholder="0.00"
                        value={taxManual2} onChange={e => setTaxManual2(e.target.value)}
                        className="wizard-total-input" inputMode="decimal" />
                    </div>
                  </div>
                )}

                <div className="wizard-nav">
                  <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                  <button type="button" className="btn-link wizard-skip"
                    onClick={() => { setTaxManual1(''); setTaxManual2(''); setTaxHasProduce(false); wizardNext(); }}>
                    Skip taxes
                  </button>
                  <button type="button" className="btn-primary" onClick={() => wizardNext()}>Next</button>
                </div>
              </div>
            );
          })()}

          {/* ITEMS */}
          {wizardStep === 'items' && (() => {
            const total = parseFloat(wizardTotal) || 0;
            const tax1 = parseFloat(taxManual1) || 0;
            const tax2 = taxHasProduce ? (parseFloat(taxManual2) || 0) : 0;
            const subtotal = parseFloat((total - tax1 - tax2).toFixed(2));
            const effectiveSubtotal = subtotal > 0 ? subtotal : total;
            const splitTotal = parseFloat(lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0).toFixed(2));
            const splitMismatch = wizardSplit && Math.abs(splitTotal - effectiveSubtotal) > 0.02;
            return (
              <div className="wizard-step">
                <div className="wizard-question">What did you purchase?</div>
                <div className="wizard-hint">
                  Subtotal for this receipt: <strong>${effectiveSubtotal.toFixed(2)}</strong>
                </div>

                {!wizardSplit && (
                  <>
                    <div className="form-group" style={{ marginBottom: '0.75rem' }}>
                      <label>What was purchased? <span className="wizard-optional">optional</span></label>
                      <input
                        type="text"
                        placeholder="e.g. office supplies, catering, cleaning products…"
                        value={lineItems[0].description}
                        onChange={e => updateLineItem(0, 'description', e.target.value)}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      <div className="form-group" style={{ flex: '2 1 150px' }}>
                        <label>Category <span className="wizard-optional">optional</span></label>
                        <select value={lineItems[0].category} onChange={e => handleCategoryChange(0, e.target.value)}>
                          <option value="">-- Category --</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                        </select>
                      </div>
                      <div className="form-group" style={{ flex: '2 1 140px' }}>
                        <label>Ministry <span className="wizard-optional">optional</span></label>
                        <select value={lineItems[0].expense_class} onChange={e => updateLineItem(0, 'expense_class', e.target.value)}>
                          <option value="">-- Ministry --</option>
                          {classes.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={wizardSplit}
                      onChange={e => {
                        const on = e.target.checked;
                        setWizardSplit(on);
                        if (on) {
                          const half = parseFloat((effectiveSubtotal / 2).toFixed(2));
                          setLineItems([
                            { ...lineItems[0], amount: String(half) },
                            { description: '', category: '', expense_class: '', amount: String(half) },
                          ]);
                        } else {
                          setLineItems([{ ...lineItems[0], amount: '' }]);
                        }
                      }}
                    />
                    Split between multiple budgets
                  </label>
                </div>

                {wizardSplit && (
                  <>
                    {lineItems.map((item, i) => (
                      <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '0.75rem', marginBottom: '0.5rem' }}>
                        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                          <label>Description <span className="wizard-optional">optional</span></label>
                          <input type="text" placeholder="e.g. Youth Ministry supplies"
                            value={item.description}
                            onChange={e => updateLineItem(i, 'description', e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <div className="form-group" style={{ flex: '2 1 130px' }}>
                            <label>Category <span className="wizard-optional">optional</span></label>
                            <select value={item.category} onChange={e => handleCategoryChange(i, e.target.value)}>
                              <option value="">-- Category --</option>
                              {categories.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                            </select>
                          </div>
                          <div className="form-group" style={{ flex: '2 1 120px' }}>
                            <label>Ministry <span className="wizard-optional">optional</span></label>
                            <select value={item.expense_class} onChange={e => updateLineItem(i, 'expense_class', e.target.value)}>
                              <option value="">-- Ministry --</option>
                              {classes.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                            </select>
                          </div>
                          <div className="form-group" style={{ flex: '1 1 90px' }}>
                            <label>Amount *</label>
                            <div className="wizard-total-input-wrap">
                              <span className="wizard-dollar">$</span>
                              <input type="number" step="0.01" min="0" placeholder="0.00"
                                value={item.amount}
                                onChange={e => updateLineItem(i, 'amount', e.target.value)}
                                className="wizard-total-input" inputMode="decimal" />
                            </div>
                          </div>
                        </div>
                        {lineItems.length > 2 && (
                          <button type="button" className="btn-link" style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}
                            onClick={() => removeLineItem(i)}>Remove</button>
                        )}
                      </div>
                    ))}
                    <button type="button" className="btn-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}
                      onClick={addLineItem}>+ Add Another Item</button>
                    <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', color: splitMismatch ? '#c0392b' : 'var(--color-text-muted)' }}>
                      Split total: <strong>${splitTotal.toFixed(2)}</strong> / Subtotal: <strong>${effectiveSubtotal.toFixed(2)}</strong>
                    </div>
                    {splitMismatch && (
                      <div style={{ fontSize: '0.85rem', color: '#c0392b', fontWeight: 600, marginTop: '0.25rem' }}>
                        Your split total does not match the subtotal.
                        <span style={{ fontWeight: 400, display: 'block', marginTop: '0.15rem' }}>
                          You can adjust the amounts on the final review screen.
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="wizard-nav">
                  <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                  <button type="button" className="btn-primary" onClick={() => wizardNext()}>Next</button>
                </div>
              </div>
            );
          })()}

          {/* MEMO */}
          {wizardStep === 'memo' && (
            <div className="wizard-step">
              <div className="wizard-question">Any notes for the financial team?</div>
              <div className="wizard-hint">
                Optional — leave a note for Caitlin or finance.
                <em style={{ display: 'block', marginTop: '0.25rem', color: 'var(--color-text-muted)' }}>
                  e.g. "Split between GC Youth High School and Middle School"
                </em>
              </div>
              <textarea
                rows={3}
                placeholder="e.g. This was for the youth ministry event…"
                value={form.memo}
                onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
                style={{ width: "100%", resize: "vertical" }}
              />
              <div className="wizard-nav">
                <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                <button type="button" className="btn-primary" onClick={() => wizardNext()}>Next</button>
              </div>
            </div>
          )}

          {/* REVIEW */}
          {wizardStep === 'review' && (() => {
            const items = wizardReviewItems || [];
            const itemsTotal = items.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
            const receiptTotal = parseFloat(wizardTotal) || 0;
            const diff = Math.abs(itemsTotal - receiptTotal);
            const mismatch = diff > 0.02;
            return (
              <div className="wizard-step">
                <div className="wizard-question">Review &amp; confirm amounts</div>
                <div className="wizard-hint">Check the amounts below against your receipt. You can edit any amount before submitting.</div>

                <div className="wizard-review-table">
                  <div className="wizard-review-row">
                    <span>Vendor</span>
                    <strong>{vendors.find(v => String(v.id) === String(form.vendor))?.name || <em>Not selected</em>}</strong>
                  </div>
                  <div className="wizard-review-row">
                    <span>Date</span>
                    <strong>{form.date}</strong>
                  </div>
                  <div className="wizard-review-row">
                    <span>Card</span>
                    <strong>
                      {selectedCard
                        ? `•••• ${selectedCard.last_four}`
                        : displayAccountName(accounts.find(a => String(a.id) === String(form.account))?.name) || <em>Not selected</em>}
                    </strong>
                  </div>
                  <div className="wizard-review-row">
                    <span>Receipt file</span>
                    <strong>{receiptFiles.length > 0 ? receiptFiles.map(f => f.name).join(', ') : <em>None attached</em>}</strong>
                  </div>
                  {form.memo && <div className="wizard-review-row"><span>Note</span><strong>{form.memo}</strong></div>}
                </div>

                <div className="wizard-review-items" style={{ marginTop: '1rem' }}>
                  <div className="wizard-review-section-label">Line Items — edit amounts if needed</div>
                  {items.map((li, i) => (
                    <div key={i} className="wizard-review-row" style={{ alignItems: 'center' }}>
                      <span style={{ flex: 1 }}>{li.description || `Item ${i + 1}`}</span>
                      <div className="wizard-total-input-wrap" style={{ width: 110 }}>
                        <span className="wizard-dollar">$</span>
                        <input
                          type="number" step="0.01" min="0" placeholder="0.00"
                          value={li.amount}
                          onChange={e => setWizardReviewItems(prev => prev.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))}
                          className="wizard-total-input"
                          inputMode="decimal"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="wizard-review-row wizard-review-total" style={{ marginTop: '0.5rem' }}>
                    <span>Line items total</span>
                    <strong style={{ color: mismatch ? '#dc3545' : 'inherit' }}>${itemsTotal.toFixed(2)}</strong>
                  </div>
                  <div className="wizard-review-row" style={{ opacity: 0.65 }}>
                    <span>Receipt total</span>
                    <strong>${receiptTotal.toFixed(2)}</strong>
                  </div>
                </div>

                {mismatch && (
                  <div className="alert alert-error" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                    Line items total (${itemsTotal.toFixed(2)}) doesn't match the receipt total (${receiptTotal.toFixed(2)}). Edit the amounts above to fix it, or submit anyway if you're sure.
                  </div>
                )}

                <button type="button" className="btn-link"
                  style={{ marginTop: "0.75rem", fontSize: "0.875rem" }} onClick={exitWizard}>
                  Edit in full form instead
                </button>

                {errorMsg && <div className="alert alert-error" style={{ marginTop: "1rem" }}>{errorMsg}</div>}

                <div className="wizard-nav" style={{ marginTop: "1.25rem" }}>
                  <button type="button" className="btn-secondary" onClick={wizardBack}>Back</button>
                  <button type="button" className="btn-primary" disabled={submitting} onClick={handleSubmit} style={{ minWidth: 140 }}>
                    {submitting ? "Submitting…" : "Submit Receipt"}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button type="button" className="btn-link"
            style={{ fontSize: "0.875rem", color: "var(--color-text-muted)" }} onClick={exitWizard}>
            Switch to full form
          </button>
        </div>
      </div>
    );
  };

  // ── Regular form ───────────────────────────────────────────────────────────
  return (
    <div className="submit-page">
      <h1>Submit a Receipt</h1>
      <p className="submit-subtitle">
        Fill in the details below and attach your receipt. The admin will review it.
      </p>

      {justSubmitted && <div className="alert alert-success">Receipt submitted! The admin will review it shortly.</div>}

      {prelink && (
        <div className="alert alert-info" style={{ marginBottom: "1rem" }}>
          This receipt will be matched to QuickBooks expense <strong>#{prelink.purchase_id}</strong>
          {prelink.vendor_name && <> — <strong>{prelink.vendor_name}</strong></>}
          {prelink.total_amount != null && <>, <strong>${parseFloat(prelink.total_amount).toFixed(2)}</strong></>}
          {prelink.txn_date && <>, <strong>{prelink.txn_date}</strong></>}.
          Fields below are pre-filled from that expense — just pick a category and ministry.
        </div>
      )}

      {/* Submission prompt modal */}
      {showPrompt && !wizardMode && (
        <div className="modal-overlay" onClick={() => dismissPrompt(false)}>
          <div className="modal-panel" style={{ maxWidth: 420, textAlign: "center" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>👋</div>
            <h2 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Hey there!</h2>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.5rem" }}>
              Want me to walk you through submitting this receipt step by step? It only takes a minute.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "1.25rem" }}>
              <button className="btn-primary" style={{ fontSize: "1rem", padding: "0.65rem 1.25rem" }} onClick={() => dismissPrompt(true)}>
                Walk me through it
              </button>
              <button className="btn-secondary" style={{ fontSize: "1rem" }} onClick={() => dismissPrompt(false)}>
                I know what I'm doing
              </button>
            </div>
            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)", cursor: "pointer" }}>
              <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
              Don't show this again
            </label>
          </div>
        </div>
      )}

      {/* Guide Me toggle */}
      {!wizardMode && (
        <div className="wizard-toggle-bar">
          <span>Not sure how to fill this out?</span>
          <button type="button" className="btn-primary" onClick={startWizard}>
            Guide Me
          </button>
        </div>
      )}

      {wizardMode ? renderWizard() : (
        <form onSubmit={handleSubmit} className="submit-form" noValidate>

          <section className="form-section">
            <h2>Receipt File</h2>
            <div
              className={`drop-zone ${dragOver ? "drag-over" : ""} ${receiptFiles.length ? "has-file" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onPaste={handlePaste}
              onClick={() => fileInputRef.current?.click()}
              tabIndex={0}
            >
              <span>Drag &amp; drop, <u>paste</u> (Ctrl+V), or <u>tap to browse</u><br />
                <small>Accepts images (JPG, PNG) and PDF · Multiple files OK</small>
              </span>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*,.pdf" multiple style={{ display: "none" }} onChange={handleFileChange} />
            {receiptFiles.length > 0 && (
              <ul style={{ listStyle: "none", padding: 0, margin: "0.5rem 0 0" }}>
                {receiptFiles.map((f, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.25rem", fontSize: "0.9rem" }}>
                    <span>📎 {f.name}</span>
                    <button type="button" className="btn-link" style={{ color: "#dc3545", fontSize: "0.8rem" }} onClick={() => removeFile(i)}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="form-section">
            <h2>Receipt Details</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Vendor</label>
                <SearchableSelect
                  options={vendors}
                  value={form.vendor}
                  onChange={id => setForm(f => ({ ...f, vendor: id }))}
                  placeholder="-- Select vendor --"
                />
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
              </div>
            </div>

            <div className="form-row">
              {user?.role === 'admin' ? (
                <div className="form-group">
                  <label>Account *</label>
                  <select value={form.account} onChange={e => setForm(f => ({ ...f, account: e.target.value }))}>
                    <option value="">-- Select account --</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{displayAccountName(a.name)}</option>)}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Card *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {cards.map(c => (
                      <button key={c.id} type="button"
                        className={`wizard-card-option card-number${form.card_id === String(c.id) ? ' selected' : ''}`}
                        style={{ flex: 'none' }}
                        onClick={() => setForm(f => ({ ...f, card_id: String(c.id) }))}>
                        •••• {c.last_four}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Payment Type</label>
                <select value={form.payment_type} onChange={e => setForm(f => ({ ...f, payment_type: e.target.value }))}>
                  <option value="Debit">Debit</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Message to Financial Team <span style={{ fontWeight: 400, color: "var(--color-text-muted)" }}>(optional)</span></label>
              <textarea
                rows={2}
                placeholder="e.g. 'this was for the youth event' or 'split between Facilities and Youth'"
                value={form.memo}
                onChange={e => setForm(f => ({ ...f, memo: e.target.value }))}
              />
            </div>
          </section>

          <section className="form-section">
            <h2>Line Items</h2>
            <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem", marginTop: "-0.75rem", marginBottom: "1rem" }}>
              Add one row per item or category.
            </p>

            {lineItems.map((item, index) => (
              <div key={index} className="line-item-row">
                <div className="line-item-fields">
                  <div className="form-group">
                    <label>Category</label>
                    <select value={item.category} onChange={e => handleCategoryChange(index, e.target.value)}>
                      <option value="">-- Category --</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                    </select>
                  </div>
                  <div className="form-group flex-2">
                    <label>Description — what was purchased?</label>
                    <input
                      type="text"
                      placeholder="e.g. 55-inch TV for security monitoring"
                      value={item.description}
                      onChange={e => updateLineItem(index, "description", e.target.value)}
                    />
                  </div>
                  <div className="form-group amount-field">
                    <label>Amount *</label>
                    <input
                      type="number" step="0.01" min="0" placeholder="0.00"
                      value={item.amount}
                      onChange={e => updateLineItem(index, "amount", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Ministry</label>
                    <select value={item.expense_class} onChange={e => updateLineItem(index, "expense_class", e.target.value)}>
                      <option value="">-- Ministry --</option>
                      {classes.map(c => <option key={c.id} value={c.id}>{displayName(c)}</option>)}
                    </select>
                  </div>
                </div>
                {lineItems.length > 1 && (
                  <button type="button" className="btn-remove-line" onClick={() => removeLineItem(index)}>✕</button>
                )}
              </div>
            ))}

            <div className="line-item-footer">
              <button type="button" className="btn-secondary" onClick={addLineItem}>+ Add Line Item</button>
              <div className="line-total">Total: <strong>${lineItemTotal.toFixed(2)}</strong></div>
            </div>
          </section>

          {errorMsg && <div className="alert alert-error" style={{ marginBottom: "1rem" }}>{errorMsg}</div>}
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}
              style={{ minWidth: 140, fontSize: "1.1rem", padding: "0.75rem 1.5rem" }}>
              {submitting ? "Submitting…" : "Submit Receipt"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
